import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bell } from "lucide-react";
import { Reveal, Eyebrow, LotusDivider } from "../components/Decorative";
import PageHero from "../components/PageHero";
import Seo from "../components/seo/Seo";
import JsonLd from "../components/seo/JsonLd";
import { TextLink } from "../components/Bits";
import TiltCard from "../components/TiltCard";
import { ROUTES, breadcrumbs } from "../lib/seo";
import { SITE_URL } from "../lib/constants";
import { trackNotifyClick } from "../lib/track";
import { EDITIONS } from "../lib/editions";

// Gurgaon has no scheduled date — this notify is worded as a "let me know
// when Antara returns", not a ticket link.
const GURGAON_RETURN_URL =
  "https://wa.me/919355322133?text=Hi%20%E2%80%94%20please%20let%20me%20know%20when%20Antara%20returns%20to%20Gurgaon";

const gurgaonEditions = EDITIONS.filter((e) => e.city === "Gurgaon");

export default function Gurgaon() {
  return (
    <>
      <Seo route={ROUTES.gurgaon} image={`${SITE_URL}/img/g5.jpg`} />
      <JsonLd data={breadcrumbs([{ name: ROUTES.gurgaon.crumb, path: ROUTES.gurgaon.path }])} />

      <PageHero
        eyebrow="Bhajan jamming in Gurgaon"
        title={<>Where Antara <span className="gold-grad italic">found its room</span></>}
        intro="Before Delhi, there was Gurgaon. The early Antara evenings played to Millennium City crowds who turned up curious and left hoarse — and made the case for everything that came after."
      />

      {/* The Gurgaon story — light */}
      <section className="bg-cream py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <Eyebrow>How it began here</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">A city that showed up to sing</h2>
            <p className="mt-6 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
              Gurgaon is a city of transplants — people who moved for work and left their family's
              evening aarti a train ride away. That turned out to be exactly the audience bhajan
              jamming was for. The early Antara editions in Sector 43 filled with a multi-generational
              crowd who didn't need convincing to sing; they'd just been waiting for somewhere to do it.
            </p>
            <p className="mt-5 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
              If you're new to the format, the{" "}
              <TextLink to={ROUTES.pillar.path}>bhajan jamming explainer</TextLink> lays out what actually
              happens in the room. If you were at one of the Gurgaon nights, the{" "}
              <TextLink to={ROUTES.editions.path}>editions archive</TextLink> has the recaps.
            </p>
            <LotusDivider />
          </Reveal>
        </div>
      </section>

      {/* Past Gurgaon editions — dark */}
      <section className="bg-[#0F0A07] py-24 md:py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center">
              <Eyebrow>Past editions</Eyebrow>
              <h2 className="mt-4 font-display text-4xl md:text-5xl" style={{ color: "#FBF6EE" }}>The Gurgaon evenings</h2>
            </div>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {gurgaonEditions.map((e, i) => (
              <Reveal key={e.slug} delay={i * 0.1}>
                <TiltCard>
                <Link
                  to={`${ROUTES.editions.path}${e.slug}/`}
                  className="group block h-full rounded-2xl overflow-hidden border transition"
                  style={{ background: "#1C1A17", borderColor: "rgba(201,169,97,0.22)" }}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={e.photos[0].src} alt={e.photos[0].alt} width={e.photos[0].width} height={e.photos[0].height} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <p className="eyebrow" style={{ color: "#C9A961" }}>{e.dateLabel} · {e.city}</p>
                    <h3 className="mt-2 font-display text-2xl" style={{ color: "#FBF6EE" }}>{e.venue}</h3>
                    <p className="mt-2 font-body text-sm" style={{ color: "#E8D9C5" }}>{e.tagline}</p>
                    <span className="mt-4 inline-flex items-center gap-2 font-body text-sm" style={{ color: "#E5C880" }}>
                      Read the recap <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* The Gurgaon chapter, closed — archive framing, no phantom date */}
      <section className="bg-cream py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <Eyebrow>The Gurgaon chapter</Eyebrow>
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-ink">Where Antara's stage life began</h2>
            <p className="mt-6 font-body text-base md:text-lg text-ink-2/85 leading-relaxed">
              Gurgaon is where Antara stepped off the river and onto a stage: the 30 May debut and the
              11 July second edition, both at American Excelsior Auditorium in Sector 43. There's no
              Gurgaon date on the calendar right now. Since then Antara has played{" "}
              <TextLink to={ROUTES.delhi.path}>Delhi</TextLink> and sold out its October shows.
            </p>

            <div className="mt-10 rounded-3xl border border-gold/25 bg-ivory-2 p-8 md:p-10">
              <h3 className="font-display text-2xl md:text-3xl text-ink">Antara will return to Gurgaon.</h3>
              <p className="mt-2 font-serif-italic text-lg text-amber">Tell us to let you know when.</p>
              <div className="mt-7 flex flex-col items-center">
                <a
                  href={GURGAON_RETURN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={trackNotifyClick}
                  className="cta-primary"
                  data-testid="gurgaon-return-notify"
                >
                  <Bell size={18} />
                  <span>Let me know about Gurgaon</span>
                  <span className="cta-arrow">→</span>
                </a>
                <p className="mt-3 font-body text-sm text-ink-2/70">
                  No date is set — this is the first place a return will be announced.
                </p>
              </div>
            </div>

            <p className="mt-8 font-body text-sm text-ink-2/70">
              New to the format? Start with{" "}
              <TextLink to={ROUTES.pillar.path}>what bhajan jamming is</TextLink>.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
